import { FormikErrors, FormikTouched, useFormik } from 'formik';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { editRestaurant } from '../../../api/restaurant';
import { useRestaurantContext } from '../../../contexts/RestaurantContext';
import useRestaurantWizardInitialValues from '../RestaurantWizard/useRestaurantWizardInitialValues';
import { RestaurantWizardFormValues, RestaurantWizardSchema } from '../RestaurantWizard/restaurantWizardSchema';
import { Dropdown, Input, ErrorText } from '../../../components/common/Form';
import { STATES } from '../../../constants/StatesConstants';
import ProfileSectionPage from '../../../components/ProfileSectionPage/ProfileSectionPage';

import './styles/_location-profile.scss';
import LocationProfileActions from './LocationProfileActions';

type AddressFormValues = RestaurantWizardFormValues['address'];

const LocationAddressPage = () => {
  const { restaurantID } = useParams<{ restaurantID: string }>();

  const { currentRestaurant, isLoading, updateRestaurant } = useRestaurantContext();

  const initialValues = useRestaurantWizardInitialValues(true, currentRestaurant);

  const formik = useFormik<RestaurantWizardFormValues>({
    initialValues,
    validationSchema: RestaurantWizardSchema.pick(['address']),
    enableReinitialize: true,
    validateOnBlur: true,

    onSubmit: async (values, { setSubmitting }) => {
      try {
        const { address } = values;

        await editRestaurant({
          address: {
            restaurantAddressID: address.restaurantAddressID,
            address1: address.address1,
            address2: address.address2,
            city: address.city,
            governingDistrict: address.governingDistrict,
            country: address.country,
            postalCode: address.postCode
          }
        });

        if (currentRestaurant) {
          updateRestaurant({
            ...currentRestaurant,
            address: {
              ...currentRestaurant.address,
              ...address,
              postalCode: address.postCode
            }
          });
        }

        toast.success('Address saved successfully.');
      } catch {
        toast.error('Failed to save address.');
      } finally {
        setSubmitting(false);
      }
    }
  });

  const handleSave = async () => {
    const errors = await formik.validateForm();

    formik.setTouched({
      ...formik.touched,
      address: {
        address1: true,
        address2: true,
        city: true,
        governingDistrict: true,
        postCode: true
      }
    });

    if (errors.address && Object.keys(errors.address).length > 0) {
      return;
    }

    await formik.submitForm();
  };

  if (isLoading || !currentRestaurant?.restaurantID) {
    return null;
  }

  if (restaurantID && Number(restaurantID) !== currentRestaurant.restaurantID) {
    return null;
  }

  const addressErrors = formik.errors.address as FormikErrors<AddressFormValues> | undefined;
  const addressTouched = formik.touched.address as FormikTouched<AddressFormValues> | undefined;

  type AddressField = 'address1' | 'address2' | 'city' | 'governingDistrict' | 'postCode';

  const getAddressError = (field: AddressField): string | undefined => {
    const error = addressErrors?.[field];
    return typeof error === 'string' ? error : undefined;
  };

  const getAddressTouched = (field: AddressField): boolean => addressTouched?.[field] === true;
  return (
    <ProfileSectionPage title="ADDRESS">
      <form
        className="location-profile-content"
        onSubmit={(event) => {
          event.preventDefault();
          handleSave().catch(() => {
            toast.error('Failed to save address.');
          });
        }}
      >
        <div className="location-address-page">
          <div className="location-address-inputs">
            <Input
              name="address.address1"
              label="STREET ADDRESS 1"
              className="location-address-street-address1-input"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.address.address1}
              touched={getAddressTouched('address1')}
              error={getAddressError('address1')}
            />

            <Input
              name="address.address2"
              label="STREET ADDRESS 2"
              className="location-address-street-address2-input"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.address.address2}
              touched={getAddressTouched('address2')}
              error={getAddressError('address2')}
            />
          </div>

          <Input
            name="address.city"
            label="CITY"
            className="location-address-city-input"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.address.city}
            touched={getAddressTouched('city')}
            error={getAddressError('city')}
          />

          <div className="location-address-state-select">
            <Dropdown
              label="STATE"
              onChange={(value: string) => formik.setFieldValue('address.governingDistrict', value)}
              value={formik.values.address.governingDistrict}
              values={[{ label: '', value: '' }, ...STATES]}
            />

            {getAddressTouched('governingDistrict') && <ErrorText error={getAddressError('governingDistrict')} />}
          </div>

          <Input
            name="address.postCode"
            label="ZIP"
            className="location-address-zip-code-input"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.address.postCode}
            touched={getAddressTouched('postCode')}
            error={getAddressError('postCode')}
          />

          <LocationProfileActions onSave={handleSave} />
        </div>
      </form>
    </ProfileSectionPage>
  );
};

export default LocationAddressPage;
